import {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useSearchParams} from "react-router-dom";
import {useTranslation} from "react-i18next";
import {Chip, Stack, Tooltip, Typography, useTheme as useMuiTheme} from "@mui/material";
import {
    MaterialReactTable,
    MRT_ColumnDef,
    useMaterialReactTable
} from "material-react-table";
import {usePermissions} from "tmwx-design-common";
import {Button, Popup, Surface, Text, useTheme} from "tmwx-design-web/src";
import {RootState} from "gui-common/redux/store";
import {EfeLog} from "gui-common/redux/data/efeLog";
import {downloadLogList, getLogList} from "gui-common/redux/action/efe/efeLogActions";
import {isEmpty} from "gui-common/appHelper";
import constants from "gui-common/constants.json";
import {efeLogActions} from "gui-common/redux/slice/efe/efeLogSlice";
import {podActions} from "gui-common/redux/slice/efe/podSlice";
import {Pod} from "gui-common/redux/data/pod";
import EfeSearchModal from "../../components/efe/EfeSearchModal";

const EfeLogs = () => {
    const {t} = useTranslation();
    const dispatch: any = useDispatch();
    const {hasRights} = usePermissions();
    const [searchParams, setSearchParams] = useSearchParams();
    const {roundness, padding} = useTheme();
    const muiTheme = useMuiTheme();

    const logList = useSelector<RootState, EfeLog[]>(state => state.efeLog.logList);
    const logLoading = useSelector<RootState, boolean>(state => state.efeLog.isLoading);
    const logCount = useSelector<RootState, number>(state => state.efeLog.logCount);
    const selectedPod = useSelector<RootState, Pod>(state => state.pod.selectedPod);

    const [label, setLabel] = useState<string>("");

    const getSeverityMeta = (level?: number) => {
        switch (level) {
            case 0:
                return { label: "Error", bg: "#dc2626", fg: "#ffffff" };
            case 1:
                return { label: "Warning", bg: "#ea580c", fg: "#ffffff" };
            case 2:
                return { label: "Info", bg: "#0284c7", fg: "#ffffff" };
            case 3:
                return { label: "Debug", bg: "#64748b", fg: "#ffffff" };
            default:
                return { label: level?.toString() ?? "-", bg: "#374151", fg: "#ffffff" };
        }
    };

    const columns = useMemo<MRT_ColumnDef<EfeLog>[]>(
        () => [
            {
                accessorKey: "timestamp",
                header: t("efe.logs.timestamp"),
                size: 180,
                Cell: ({cell}) => (
                    <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
                        {cell.getValue<string>() || "-"}
                    </Typography>
                ),
            },
            {
                accessorKey: "level",
                header: t("efe.logs.severity"),
                size: 110,
                Cell: ({cell}) => {
                    const meta = getSeverityMeta(cell.getValue<number | undefined>());
                    return (
                        <Chip
                            size="small"
                            label={meta.label}
                            sx={{
                                backgroundColor: meta.bg,
                                color: meta.fg,
                                fontWeight: 700,
                                borderColor: meta.bg,
                            }}
                            variant="filled"
                        />
                    );
                },
            },
            {
                accessorKey: "service",
                header: t("efe.logs.service"),
                size: 220,
                Cell: ({cell}) => (
                    <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>
                        {cell.getValue<string>() || "-"}
                    </Typography>
                ),
            },
            {
                accessorKey: "message",
                header: t("efe.logs.message"),
                size: 560,
                Cell: ({cell}) => (
                    <Typography variant="body2" sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                        {cell.getValue<string>() || "-"}
                    </Typography>
                ),
            },
        ],
        [t]
    );

    const handleGetList = () => {
        if (!isEmpty(selectedPod) && hasRights(["efe_log_list"])) {
            dispatch(getLogList(selectedPod.podId));
        }
    };

    const handleDownload = () => {
        if (!isEmpty(selectedPod) && hasRights(["efe_log_list"])) {
            dispatch(downloadLogList(selectedPod.podId));
        }
    };

    useEffect(() => {
        const podId = localStorage.getItem(constants.POD_ID);
        const systemId = localStorage.getItem(constants.SYSTEM_ID);
        const machineName = localStorage.getItem(constants.MACHINE_NAME);

        if (podId != null && systemId != null && machineName != null) {
            dispatch(podActions.setSelectedPod({
                id: -1,
                customerName: machineName,
                companyName: "",
                location: {} as any,
                firmware: {} as any,
                podId: podId,
                systemId: systemId,
                phaseCount: "",
                lastMetricTime: 0
            }));
        }
    }, [dispatch]);

    useEffect(() => {
        if (!isEmpty(selectedPod.podId) && !isEmpty(selectedPod.systemId) && !isEmpty(selectedPod.customerName)) {
            setLabel(`${selectedPod.podId} - ${selectedPod.systemId} - ${selectedPod.customerName}`);
            setSearchParams({
                podId: selectedPod.podId,
                systemId: selectedPod.systemId,
                machineName: selectedPod.customerName
            });

            if (hasRights(["efe_log_list"])) {
                dispatch(getLogList(selectedPod.podId));
            }
        } else {
            dispatch(efeLogActions.setLogList([]));
            dispatch(efeLogActions.setLogCount(0));
        }
    }, [dispatch, hasRights, selectedPod, setSearchParams]);

    useEffect(() => {
        const searchPodId = searchParams.get("podId") as string;
        const searchSystemId = searchParams.get("systemId") as string;
        const searchMachineName = searchParams.get("machineName") as string;

        if (searchPodId != null && searchSystemId != null && searchMachineName != null) {
            localStorage.setItem(constants.POD_ID, searchPodId);
            localStorage.setItem(constants.SYSTEM_ID, searchSystemId);
            localStorage.setItem(constants.MACHINE_NAME, searchMachineName);

            dispatch(podActions.setSelectedPod({
                id: -1,
                customerName: searchMachineName,
                companyName: "",
                location: {} as any,
                firmware: {} as any,
                podId: searchPodId,
                systemId: searchSystemId,
                phaseCount: "",
                lastMetricTime: 0
            }));
        }
    }, [dispatch, searchParams]);

    const table = useMaterialReactTable({
        columns,
        data: logList,
        enableColumnFilters: true,
        enableColumnOrdering: true,
        enableDensityToggle: false,
        enableFullScreenToggle: false,
        enableGlobalFilter: true,
        enableHiding: true,
        enablePagination: true,
        enableStickyHeader: true,
        enableSorting: true,
        initialState: {
            density: "compact",
            pagination: {
                pageIndex: 0,
                pageSize: 25,
            },
        },
        muiTableContainerProps: {
            sx: {
                maxHeight: "calc(100vh - 180px)",
                borderRadius: `${roundness}px`,
            },
        },
        muiTablePaperProps: {
            elevation: 0,
            sx: {
                borderRadius: `${roundness}px`,
                border: `1px solid ${muiTheme.palette.divider}`,
                overflow: "hidden",
            },
        },
        muiTableBodyCellProps: {
            sx: {
                verticalAlign: "top",
            },
        },
        muiTableHeadCellProps: {
            sx: {
                backgroundColor: muiTheme.palette.background.paper,
                fontWeight: 700,
            },
        },
        state: {
            isLoading: logLoading,
            showProgressBars: logLoading,
        },
        renderTopToolbarCustomActions: () => (
            <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
                sx={{ px: 1, py: 1 }}
            >
                <EfeSearchModal label={label} />
                <Popup
                    style={{ marginRight: 4 }}
                    triggerComponent={
                        <Button status="info" variant="outlined" onPress={handleDownload} disabled={isEmpty(selectedPod)}>
                            {t("download")}
                        </Button>
                    }
                    type="hover"
                >
                    <Surface style={{ padding, minWidth: 260 }} elevation={1}>
                        <Text>{t("downloadLogsCsv")}</Text>
                    </Surface>
                </Popup>
                <Tooltip title={selectedPod.podId ? selectedPod.podId : ""} placement="top" arrow>
                    <Typography variant="body2" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                        {logCount} logs
                    </Typography>
                </Tooltip>
            </Stack>
        ),
    });

    return (
        <Surface style={{ height: "100vh", padding: 16, display: "flex", flexDirection: "column", gap: 12 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={2}>
                <div>
                    <Text bold style={{ fontSize: 22 }}>
                        {t("efe.efeLog")}
                    </Text>
                    <Text variant="body" style={{ opacity: 0.72 }}>
                        {selectedPod?.podId ? `${selectedPod.podId} · ${selectedPod.systemId} · ${selectedPod.customerName}` : t("selectEfeLogMachine")}
                    </Text>
                </div>
                <Button status="neutral" variant="outlined" onPress={handleGetList} disabled={isEmpty(selectedPod)}>
                    {t("refresh")}
                </Button>
            </Stack>

            <div style={{ flex: 1, minHeight: 0 }}>
                <MaterialReactTable table={table} />
            </div>
        </Surface>
    );
};

export default EfeLogs;
